import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  USER_OPERATIONS,
  RESOURCE_TYPE,
  API_RESOURCE_URLS,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  LINE_TYPE,
} from "../../constants";
import defaultStyles from "./LineoutApproval.module.css";
import "../../cssModules/switch.css";
import { BackButton } from "..";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useHistory } from "react-router-dom";
import { API } from "../../apis/api";
import {
  downloadCsv,
  exportToExcel,
  getMonthNameWithYear,
} from "../../utils/utils";
import { buildErrorMessage } from "../../apis/calls";
import moment from "moment";
import stylesEvr from "../EvrNumberToolbar/EvrNumberToolbar.module.css";
import EmergencyASNApprovalModal from "../EmergencyASNApprovalModal/EmergencyASNApprovalModal";
import LineDropdownSelector from "../LineDropdownSelector/LineDropdownSelector";

const LineoutApproval = ({ setHasActiveEdits, handleCountChange }) => {
  const ROW_HEIGHT = 38;
  const RESOURCE_OPERATION_UPDATE = USER_OPERATIONS.UPDATE_EVR_QUANTITY;
  const RESOURCE_OPERATION_ADD_CSV = USER_OPERATIONS.ADD_EVR_DATA;
  const {
    showSuccess,
    showInternalError,
    showError,
    showLoading,
    closePopUp,
  } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const [isAsnApprovalModalOpen, setISAsnApprovalModalOpen] = useState(false);
  const isLucknowProlifePlant = plant?.label === "Lucknow-Prolife";
  const [eAsnFilteredRows, setLinOutFilteredRows] = useState([]);
  const history = useHistory();

  const selectedLine = line;

  const [styles, setStyles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [asnCount, setAsnCount] = useState(0);
  const setCount = (count) => {
    setAsnCount(count);
  };

  const fixedColumns = [
    {
      width: 140,
      title: "AgSN ID",
      field: "agsnId",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "ASN",
      field: "asn",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "AgSN",
      field: "agsn",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Part Number",
      field: "partNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Part Type",
      field: "partType",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Procurement Plant",
      field: "procurementPlant",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Planned Production Date",
      field: "partPlannedProductionDate",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Reason",
      field: "reason",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Agency",
      field: "agency",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Comment",
      field: "comment",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: "Root Material Number",
      field: "rootMaterialNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 250,
      title: "Root Material Description",
      field: "rootMaterialDescription",
      enableSearch: true,
      enableFilter: true,
    },
  ];

  const redirectToApplicationLandingPage = () => {
    history.push("/");
  };

  const getLineOutData = async (fromDate) => {
    if (plant.value && selectedLine?.value) {
      try {
        let response = await API.get(
          API_RESOURCE_URLS.getLineoutApprovalData(selectedLine.value),
        );
        const tableData = response.data.map((row) => ({
          ...row,
          id: row.agsnId,
        }));
        setRowsData({
          loading: false,
          data: tableData,
          filteredData: tableData,
        });
      } catch (err) {
        console.log(err);
        showInternalError({ error: err });
      }
    }
  };
  const prepareJsonHeaderAndValueFormats = () => {
    const monthPlanHeaderAndValueFormats = [
      {
        label: "Material Number",
        value: (row) => row.vc,
      },
      {
        label: "Category",
        value: (row) => row.description,
      },
      {
        label: "Market",
        value: (row) => row.validFrom,
      },
      {
        label: "Country Code",
        value: (row) => row.validTo,
      },
      {
        label: "country",
        value: (row) => row.countryCode,
      },
      {
        label: "Actual Quantity",
        value: (row) => row.completed,
      },
    ].filter((c) => c);

    return [...monthPlanHeaderAndValueFormats];
  };

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    getLineOutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, selectedLine?.value]);

  const openLineoutDateModal = () => setISAsnApprovalModalOpen(true);
  const closeLineoutDateModal = () => setISAsnApprovalModalOpen(false);

  const handleApprove = (selectedRows) => {
    if (!selectedRows?.length) {
      return;
    }

    const uniqueLines = [...new Set(selectedRows.map((row) => row.line))];

    if (uniqueLines.length > 1) {
      showError({
        contextText: "Multiple ASNs selected should belong to the same line.",
        info: "",
      });
      return;
    }

    setLinOutFilteredRows(selectedRows);
    openLineoutDateModal();
  };
  const checkIfRowIsSelected = (selectedRows) => {
    return selectedRows.length > 0;
  };

  const handleLineoutApprove = async (selectedRows, comment, status) => {
    try {
      const payload = selectedRows.map((row) => ({
        asnId: row.agsnId,
        reason: row.reason,
        agency: row.agency,
        comment: comment,
      }));
      showLoading();
      let response;
      if (status == "REJECTED") {
        response = await API.patch(
          API_RESOURCE_URLS.AGSN_LINE_OUT_REJECT(selectedLine.value),
          {
            updates: payload,
          },
        );
        if (response.status === 200 && response.data?.length) {
          showSuccess({
            contextText: DISPLAY_MESSAGES.REJET_LINOUT,
            info: "",
          });
        } else if (response.status === 403) {
          showError({
            contextText: DISPLAY_MESSAGES.FORBIDDEN_ACCESS,
            info: "",
          });
        } else if (response.status === 204) {
          showSuccess({
            contextText: DISPLAY_MESSAGES.REJET_LINOUT,
            info: "",
          });
        }
      } else if (status == "APPROVED") {
        response = await API.patch(
          API_RESOURCE_URLS.VC_ASN_LINE_OUT_UPDATED(selectedLine.value),
          {
            updates: payload,
          },
        );

        if (response.status === 200 && response.data?.length) {
          showSuccess({
            contextText: isLucknowProlifePlant
              ? DISPLAY_MESSAGES.PART_STATUS_LINEOUT
              : DISPLAY_MESSAGES.VC_STATUS_LINEOUT,
            info: "",
          });
        } else if (response.status === 403) {
          showError({
            contextText: DISPLAY_MESSAGES.FORBIDDEN_ACCESS,
            info: "",
          });
        } else if (response.status === 202 && response.data?.length) {
          showSuccess({
            contextText: response.data[0],
            info: "",
          });
        }
      }
      await getLineOutData();
    } catch (err) {
      console.error(buildErrorMessage(err));
      const errorMessage =
        err?.request?.responseText || showInternalError({ error: err });
      showError({
        contextText: errorMessage,
        info: "",
        error: buildErrorMessage(err),
      });
    }
    return false;
  };

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          action="Linout Approval"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>
      <div className={stylesEvr.RgtTools}>
        <div className={stylesEvr.eAgsnToolbar}>
          <div className={styles.lineSelect}>
            <span className={styles.selectLineSpan}>Select line</span>
            <LineDropdownSelector />
          </div>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={[...fixedColumns]}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            primaryAction={{
              name: "Approve/Reject",
              actionFn: (filteredData) => handleApprove(filteredData),
              authOperation: RESOURCE_OPERATION_UPDATE,
              shouldEnable: (selectedRows) =>
                checkIfRowIsSelected(selectedRows),
            }}
          />
        </div>
      </div>
      {isAsnApprovalModalOpen ? (
        <EmergencyASNApprovalModal
          open={isAsnApprovalModalOpen}
          handleClose={closeLineoutDateModal}
          title="Linout Approval"
          rowsData={eAsnFilteredRows}
          lineBackgroundColor="#82C1A7"
          handleSubmit={handleLineoutApprove}
        />
      ) : null}
    </>
  );
};

LineoutApproval.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  LineoutApproval,
  false,
  RESOURCE_TYPE.EVR_QUANTITY,
);
